/**
 * SortableTableHeader — table column header cliccabile per sort.
 *
 * Pattern shared per tutte le admin tables (sprint admin_sort_all_tables
 * 30/05/26). Click su una colonna sortable:
 *  - Se NON è il sort corrente → diventa corrente con dir=desc (default per createdAt-like)
 *  - Se È il sort corrente → toggle asc/desc
 *  - Re-click 3 volte sulla stessa colonna → resetta al default (sortBy=null)
 *
 * Visualmente:
 *  - Icone Lucide ArrowUp / ArrowDown / ArrowUpDown (idle)
 *  - Hover bg-accent/30 + cursor-pointer
 *  - Colonna attiva: bg primary/5 + icona colored
 *
 * Uso:
 *   <th className="text-left py-3 px-4">
 *     <SortableTableHeader
 *       label="Email"
 *       field="email"
 *       sortBy={sortBy}
 *       sortDir={sortDir}
 *       onChange={(field, dir) => { setSortBy(field); setSortDir(dir); }}
 *     />
 *   </th>
 *
 * Per colonne NON sortable: lascia <th> normale con testo statico.
 */
import { ArrowUp, ArrowDown, ArrowUpDown } from 'lucide-react';

import { cn } from '@/lib/utils';

export type SortDir = 'asc' | 'desc';

export interface SortableTableHeaderProps {
  /** Label visualizzata (es. "Email", "Creato il") */
  label: string;
  /** Field name passato al BE come sortBy (es. "email", "createdAt") */
  field: string;
  /** Stato corrente sort (null = default BE) */
  sortBy: string | null;
  sortDir: SortDir;
  /**
   * Callback su click. Riceve nuovo (field, dir) oppure (null, dir) quando
   * l'utente clicca la 3a volta sulla stessa colonna (reset al default).
   */
  onChange: (field: string | null, dir: SortDir) => void;
  /** Allineamento testo del wrapper (default 'left') */
  align?: 'left' | 'right' | 'center';
  /** Default direction al primo click (default 'desc') */
  defaultDir?: SortDir;
}

export function SortableTableHeader({
  label,
  field,
  sortBy,
  sortDir,
  onChange,
  align = 'left',
  defaultDir = 'desc',
}: SortableTableHeaderProps) {
  const isActive = sortBy === field;
  const Icon = !isActive ? ArrowUpDown : sortDir === 'asc' ? ArrowUp : ArrowDown;

  const handleClick = () => {
    if (!isActive) {
      // Primo click: attivo questa colonna con defaultDir
      onChange(field, defaultDir);
      return;
    }
    // Già attivo: toggle dir; al 3° click reset
    if (sortDir === defaultDir) {
      onChange(field, defaultDir === 'desc' ? 'asc' : 'desc');
    } else {
      // Reset al default BE
      onChange(null, defaultDir);
    }
  };

  return (
    <button
      type="button"
      onClick={handleClick}
      className={cn(
        'inline-flex items-center gap-1 group transition-colors',
        align === 'right' && 'flex-row-reverse',
        align === 'center' && 'justify-center',
        'hover:text-foreground',
        isActive ? 'text-foreground font-semibold' : 'text-muted-foreground font-medium',
      )}
      title={
        isActive
          ? `Ordinato per ${label} ${sortDir === 'asc' ? '↑' : '↓'} — click per ${sortDir === defaultDir ? 'invertire' : 'rimuovere ordine'}`
          : `Click per ordinare per ${label}`
      }
    >
      <span>{label}</span>
      <Icon
        className={cn(
          'h-3 w-3 shrink-0 transition-opacity',
          isActive ? 'opacity-100 text-primary' : 'opacity-30 group-hover:opacity-60',
        )}
      />
    </button>
  );
}
